/**
 * Device Import Modal
 * Modal for importing returned devices from Excel
 */

import React, { useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import api from '@/services/api/client';
import { BaseModal } from '@/components/common/BaseModal';

interface DeviceImportModalProps {
  onClose: () => void;
  onSuccess: () => void;
  language: string;
}

interface ImportResult {
  successful: number;
  failed: number;
  manualRequired: number;
  results: Array<{
    imei: string;
    success: boolean;
    requiresManual?: boolean;
    error?: string;
    manualReason?: string;
  }>;
}

const DeviceImportModal: React.FC<DeviceImportModalProps> = ({ onClose, onSuccess, language }) => {
  const [imeis, setImeis] = useState<string[]>([]);
  const [rawInput, setRawInput] = useState('');
  const [settings, setSettings] = useState({
    setStatusToStock: true,
    deactivateSimCards: false,
    disconnectFromVehicle: true
  });
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  
  const parseInput = () => {
    const lines = rawInput.split(/[\n,;]+/).map(l => l.trim()).filter(l => l && /^\d{14,16}$/.test(l));
    setImeis([...new Set(lines)]);
  };
  
  const handleImport = async () => {
    if (imeis.length === 0) {
      alert(language === 'sv' ? 'Inga giltiga IMEI-nummer' : 'No valid IMEI numbers');
      return;
    }
    
    try {
      setImporting(true);
      const response = await api.post('/api/device-management/import/returns', {
        imeis,
        settings
      });
      
      if (response.data.success) {
        setResult(response.data.data);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };
  
  const title = language === 'sv' ? 'Importera returnerade enheter' : 'Import returned devices';
  const subtitle = language === 'sv'
    ? 'Klistra in IMEI-nummer från Excel (en per rad)'
    : 'Paste IMEI numbers from Excel (one per line)';

  return (
    <BaseModal
      isOpen={true}
      onClose={result ? onSuccess : onClose}
      title={title}
      subtitle={subtitle}
      size="2xl"
      className="max-h-[90vh] overflow-hidden flex flex-col"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <button
            type="button"
            onClick={result ? onSuccess : onClose}
            className="px-4 py-2 bg-bgDefault border border-borderDefault rounded-lg text-textDefault hover:bg-bgHover transition-colors"
          >
            {result
              ? (language === 'sv' ? 'Stäng' : 'Close')
              : (language === 'sv' ? 'Avbryt' : 'Cancel')}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleImport}
              disabled={imeis.length === 0 || importing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {importing && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
              {language === 'sv' ? 'Importera' : 'Import'} ({imeis.length})
            </button>
          )}
        </div>
      }
    >
        <div className="flex-1 overflow-y-auto min-h-0">
          {result ? (
            // Result view
            <div>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-green-100 dark:bg-green-900/30 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-green-600">{result.successful}</div>
                  <div className="text-sm text-green-600">{language === 'sv' ? 'Lyckade' : 'Successful'}</div>
                </div>
                <div className="bg-red-100 dark:bg-red-900/30 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-red-600">{result.failed}</div>
                  <div className="text-sm text-red-600">{language === 'sv' ? 'Misslyckade' : 'Failed'}</div>
                </div>
                <div className="bg-orange-100 dark:bg-orange-900/30 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-orange-600">{result.manualRequired}</div>
                  <div className="text-sm text-orange-600">{language === 'sv' ? 'Kräver manuell' : 'Manual required'}</div>
                </div>
              </div>
              
              {result.results?.filter(r => r.requiresManual || !r.success).length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-medium text-textDefault mb-2">
                    {language === 'sv' ? 'Behöver åtgärd:' : 'Needs attention:'}
                  </h3>
                  {result.results.filter(r => r.requiresManual || !r.success).map(r => (
                    <div key={r.imei} className="bg-bgDefault p-3 rounded-lg text-sm">
                      <span className="font-mono">{r.imei}</span>
                      <span className="text-red-500 ml-2">{r.error || r.manualReason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Input view
            <>
              <textarea
                value={rawInput}
                onChange={(e) => setRawInput(e.target.value)}
                onBlur={parseInput}
                placeholder={language === 'sv' 
                  ? '869616066806822\n869616066806823\n869616066806824' 
                  : '869616066806822\n869616066806823\n869616066806824'}
                className="w-full h-48 p-4 bg-bgDefault border border-borderDefault rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              
              {imeis.length > 0 && (
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <span className="text-blue-600 font-medium">
                    {imeis.length} {language === 'sv' ? 'giltiga IMEI-nummer hittade' : 'valid IMEI numbers found'}
                  </span>
                </div>
              )}
              
              <div className="mt-6 space-y-3">
                <h3 className="font-medium text-textDefault">
                  {language === 'sv' ? 'Inställningar:' : 'Settings:'}
                </h3>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.setStatusToStock}
                    onChange={(e) => setSettings(s => ({ ...s, setStatusToStock: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-textDefault">
                    {language === 'sv' ? 'Sätt status till LAGER' : 'Set status to STOCK'}
                  </span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.disconnectFromVehicle}
                    onChange={(e) => setSettings(s => ({ ...s, disconnectFromVehicle: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-textDefault">
                    {language === 'sv' ? 'Koppla bort från fordon' : 'Disconnect from vehicle'}
                  </span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.deactivateSimCards}
                    onChange={(e) => setSettings(s => ({ ...s, deactivateSimCards: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-textDefault">
                    {language === 'sv' ? 'Inaktivera SIM-kort hos Jasper' : 'Deactivate SIM cards at Jasper'}
                  </span>
                </label>
              </div>
            </>
          )}
        </div>
    </BaseModal>
  );
};

export default DeviceImportModal;
